import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ReturnOrderContainerSummary,
  ReturnOrderReason,
} from '@/graphql/generatedTypes';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Routes } from '@/utils/routes';
import { Link } from 'react-router-dom';

import { ItemTypeCell } from './DetailFormParts';
import type { ItemFormState } from './types';

const RETURN_REASON_LABEL_KEYS: Record<ReturnOrderReason, string> = {
  [ReturnOrderReason.Termination]: 'Order:returnReasonTermination',
  [ReturnOrderReason.Replacement]: 'Order:returnReasonReplacement',
  [ReturnOrderReason.Overstock]: 'Order:returnReasonOverstock',
  [ReturnOrderReason.Defect]: 'Order:returnReasonDefect',
  [ReturnOrderReason.Other]: 'Order:returnReasonOther',
};

interface InboundAnnouncementSectionProps {
  returnReasons: ReturnOrderReason[];
  returnReasonText: string | null | undefined;
  items: ItemFormState[];
  packageWeights: number[];
  returnContainers: Pick<
    ReturnOrderContainerSummary,
    'lines' | 'removed_containers'
  > | null;
}

export function InboundAnnouncementSection({
  returnReasons,
  returnReasonText,
  items,
  packageWeights,
  returnContainers,
}: InboundAnnouncementSectionProps) {
  const containerLines = new Map(
    (returnContainers?.lines ?? []).map((line) => [
      line.container_type_id,
      line,
    ]),
  );
  const removedContainers = returnContainers?.removed_containers ?? [];
  const typeNames = new Map(
    items
      .filter((item) => item.kind === 'container')
      .map((item) => [item.type_id, item.typeName]),
  );
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{i18n.t('Order:sectionReturnReasons')}</CardTitle>
        </CardHeader>
        <CardContent className="tw:flex tw:flex-col tw:gap-3">
          {returnReasons.length === 0 && !returnReasonText && (
            <p className="tw:text-muted-foreground">—</p>
          )}
          {returnReasons.length > 0 && (
            <div className="tw:flex tw:flex-wrap tw:gap-1.5">
              {returnReasons.map((reason) => (
                <Badge key={reason} variant="subtle">
                  {i18n.t(RETURN_REASON_LABEL_KEYS[reason])}
                </Badge>
              ))}
            </div>
          )}
          {returnReasonText && (
            <p className="tw:rounded-lg tw:bg-muted/50 tw:px-3 tw:py-2 tw:whitespace-pre-wrap">
              {returnReasonText}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{i18n.t('Order:sectionAnnouncedItems')}</CardTitle>
          <CardDescription>
            {packageWeights.length === 0
              ? i18n.t('Order:tableNoPackageWeights')
              : i18n.t('Order:packageWeightsSummary', {
                  count: packageWeights.length,
                  weights: packageWeights
                    .map((weight) => `${weight} ${i18n.t('Common:kg')}`)
                    .join(', '),
                })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="tw:text-muted-foreground">
              {i18n.t('Order:tableNoItems')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{i18n.t('Order:tableItemType')}</TableHead>
                  <TableHead className="tw:text-right">
                    {i18n.t('Order:tableAmount')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const line =
                    item.kind === 'container'
                      ? containerLines.get(item.type_id)
                      : undefined;
                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <ItemTypeCell
                          name={item.typeName}
                          imageUrl={item.imageUrl}
                          hexCode={item.hexCode}
                          caption={
                            line &&
                            i18n.t('Order:announcedContainerBreakdown', {
                              identified: line.identified,
                              counted: line.counted,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="tw:text-right tw:tabular-nums">
                        {line ? line.amount : item.amount}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {removedContainers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{i18n.t('Order:sectionRemovedContainers')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableBody>
                {removedContainers.map((container) => (
                  <TableRow key={`${container.id}-${container.removed_at}`}>
                    <TableCell>
                      <Link
                        to={Routes.container(container.id)}
                        className="tw:text-sm tw:font-medium"
                      >
                        {container.name ?? container.short_id ?? container.id}
                      </Link>
                      <span className="tw:text-muted-foreground tw:text-sm">
                        {' · '}
                        {typeNames.get(container.container_type_id) ??
                          container.container_type_id}
                      </span>
                    </TableCell>
                    <TableCell className="tw:text-sm">
                      {i18n.t(
                        `Order:removedContainerReason_${container.reason}`,
                      )}
                    </TableCell>
                    <TableCell className="tw:text-right tw:text-sm tw:text-muted-foreground">
                      {new Date(container.removed_at).toLocaleDateString(
                        i18n.language,
                        getDateFormatOptions(true),
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </>
  );
}
